import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Save, Loader2, Plus, Trash2, RotateCcw } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { UploadWidget } from "@/components/admin/UploadWidget";

const ICON_OPTIONS = ["TrendingUp", "Users", "Globe", "Layers"];

function FieldGroup({ title, children }) {
    return (
        <section className="rounded-xl border border-border bg-white p-6">
            <h2 className="font-heading text-lg font-bold">{title}</h2>
            <div className="mt-5 space-y-4">{children}</div>
        </section>
    );
}

function TextField({ label, value, onChange, placeholder, testId, textarea, rows = 3 }) {
    const cls = "w-full rounded-md border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
    return (
        <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
            {textarea ? (
                <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className={`mt-1.5 ${cls}`} />
            ) : (
                <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className={`mt-1.5 ${cls}`} />
            )}
        </div>
    );
}

export default function AdminHomeSettings() {
    useSEO({ title: "Home Page — Settings | RKS Systems" });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/settings/home").then(({ data }) => setData(data)).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const setPath = (path, value) => {
        setData((prev) => {
            const next = JSON.parse(JSON.stringify(prev || {}));
            const parts = path.split(".");
            let cur = next;
            for (let i = 0; i < parts.length - 1; i++) {
                if (typeof cur[parts[i]] !== "object" || cur[parts[i]] === null) cur[parts[i]] = {};
                cur = cur[parts[i]];
            }
            cur[parts[parts.length - 1]] = value;
            return next;
        });
        setSuccess(false);
    };

    const setMetric = (i, key, value) => {
        setData((prev) => {
            const next = JSON.parse(JSON.stringify(prev || {}));
            next.metrics = next.metrics || [];
            next.metrics[i] = { ...next.metrics[i], [key]: value };
            return next;
        });
        setSuccess(false);
    };

    const addMetric = () => {
        setData((prev) => ({ ...prev, metrics: [...(prev?.metrics || []), { value: "", label: "", icon: "TrendingUp" }] }));
    };
    const delMetric = (i) => {
        setData((prev) => ({ ...prev, metrics: (prev?.metrics || []).filter((_, idx) => idx !== i) }));
    };

    const save = async () => {
        setSaving(true);
        setError("");
        setSuccess(false);
        try {
            const { key, _id, created_at, updated_at, ...rest } = data || {};
            await api.put("/admin/settings/home", rest);
            setSuccess(true);
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !data) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>;

    const hero = data.hero || {};
    const metrics = data.metrics || [];
    const cta = data.cta_block || {};

    return (
        <div className="p-8 max-w-5xl" data-testid="page-admin-home-settings">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <Link to="/admin/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ChevronLeft className="h-3 w-3" /> Back to dashboard</Link>
                    <h1 className="mt-1 font-heading text-3xl font-bold">Home Page Settings</h1>
                    <p className="text-muted-foreground mt-1 text-sm">Edit the hero, metrics, section titles and CTA that appear on the public home page.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} data-testid="home-settings-reset" className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-2 text-sm font-semibold hover:bg-secondary"><RotateCcw className="h-3.5 w-3.5" /> Reload</button>
                    <button onClick={save} disabled={saving} data-testid="home-settings-save" className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>

            {success && <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" data-testid="home-settings-saved">Saved. Changes are live on the home page.</div>}
            {error && <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            <div className="mt-8 space-y-6">
                <FieldGroup title="Hero section">
                    <TextField label="Badge (small text above headline)" value={hero.badge} onChange={(v) => setPath("hero.badge", v)} testId="field-hero-badge" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <TextField label="Headline — before highlight" value={hero.headline_pre} onChange={(v) => setPath("hero.headline_pre", v)} testId="field-hero-headline-pre" />
                        <TextField label="Highlighted word" value={hero.headline_highlight} onChange={(v) => setPath("hero.headline_highlight", v)} testId="field-hero-headline-highlight" />
                        <TextField label="Headline — after highlight" value={hero.headline_post} onChange={(v) => setPath("hero.headline_post", v)} testId="field-hero-headline-post" />
                    </div>
                    <TextField label="Sub-headline paragraph" value={hero.subheadline} onChange={(v) => setPath("hero.subheadline", v)} testId="field-hero-subheadline" textarea rows={3} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <TextField label="Primary CTA label" value={hero.primary_cta_label} onChange={(v) => setPath("hero.primary_cta_label", v)} testId="field-hero-primary-cta-label" />
                        <TextField label="Primary CTA link" value={hero.primary_cta_link} onChange={(v) => setPath("hero.primary_cta_link", v)} testId="field-hero-primary-cta-link" />
                        <TextField label="Secondary CTA label" value={hero.secondary_cta_label} onChange={(v) => setPath("hero.secondary_cta_label", v)} testId="field-hero-secondary-cta-label" />
                        <TextField label="Secondary CTA link" value={hero.secondary_cta_link} onChange={(v) => setPath("hero.secondary_cta_link", v)} testId="field-hero-secondary-cta-link" />
                    </div>
                    <UploadWidget label="Hero image" value={hero.image} onChange={(v) => setPath("hero.image", v)} accept="image" testId="upload-hero-image" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <TextField label="Stars rating" value={hero.trust_stars_rating} onChange={(v) => setPath("hero.trust_stars_rating", v)} testId="field-hero-stars-rating" />
                        <TextField label="Stars label" value={hero.trust_stars_label} onChange={(v) => setPath("hero.trust_stars_label", v)} testId="field-hero-stars-label" />
                        <TextField label="Availability figure" value={hero.trust_availability} onChange={(v) => setPath("hero.trust_availability", v)} testId="field-hero-availability" />
                        <TextField label="Availability label" value={hero.trust_availability_label} onChange={(v) => setPath("hero.trust_availability_label", v)} testId="field-hero-availability-label" />
                    </div>
                    <TextField label="Trusted-by tagline (on hero image card)" value={hero.trusted_by_count} onChange={(v) => setPath("hero.trusted_by_count", v)} testId="field-hero-trusted-by" />
                </FieldGroup>

                <FieldGroup title="Metrics strip">
                    {metrics.map((m, i) => (
                        <div key={i} className="grid grid-cols-12 gap-3 items-end rounded-lg border border-border bg-secondary/30 p-3">
                            <div className="col-span-3">
                                <TextField label="Value" value={m.value} onChange={(v) => setMetric(i, "value", v)} testId={`field-metric-${i}-value`} />
                            </div>
                            <div className="col-span-6">
                                <TextField label="Label" value={m.label} onChange={(v) => setMetric(i, "label", v)} testId={`field-metric-${i}-label`} />
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Icon</label>
                                <select value={m.icon || "TrendingUp"} onChange={(e) => setMetric(i, "icon", e.target.value)} data-testid={`field-metric-${i}-icon`} className="mt-1.5 w-full rounded-md border border-input bg-white px-2 py-2.5 text-sm">
                                    {ICON_OPTIONS.map((o) => (<option key={o}>{o}</option>))}
                                </select>
                            </div>
                            <div className="col-span-1">
                                <button onClick={() => delMetric(i)} className="h-10 w-10 rounded-md border border-input inline-flex items-center justify-center text-muted-foreground hover:text-red-600" data-testid={`del-metric-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                        </div>
                    ))}
                    <button onClick={addMetric} className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-input px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary" data-testid="add-metric"><Plus className="h-3.5 w-3.5" /> Add metric</button>
                </FieldGroup>

                {[
                    { path: "services_section", label: "Services section heading" },
                    { path: "industries_section", label: "Industries section heading" },
                    { path: "stories_section", label: "Success Stories heading" },
                    { path: "engagement_section", label: "Engagement Models heading" },
                    { path: "blog_section", label: "Blog section heading" },
                ].map((s) => {
                    const v = data[s.path] || {};
                    return (
                        <FieldGroup key={s.path} title={s.label}>
                            <TextField label="Eyebrow" value={v.eyebrow} onChange={(x) => setPath(`${s.path}.eyebrow`, x)} testId={`field-${s.path}-eyebrow`} />
                            <TextField label="Title" value={v.title} onChange={(x) => setPath(`${s.path}.title`, x)} testId={`field-${s.path}-title`} />
                            {typeof v.description !== "undefined" || ["services_section","industries_section","engagement_section"].includes(s.path) ? (
                                <TextField label="Description" value={v.description} onChange={(x) => setPath(`${s.path}.description`, x)} testId={`field-${s.path}-description`} textarea rows={2} />
                            ) : null}
                        </FieldGroup>
                    );
                })}

                <FieldGroup title="Products section (dark)">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <TextField label="Title — before highlight" value={data.prototypes_section?.title_pre} onChange={(v) => setPath("prototypes_section.title_pre", v)} testId="field-products-title-pre" />
                        <TextField label="Highlighted word" value={data.prototypes_section?.title_highlight} onChange={(v) => setPath("prototypes_section.title_highlight", v)} testId="field-products-title-highlight" />
                        <TextField label="Title — after highlight" value={data.prototypes_section?.title_post} onChange={(v) => setPath("prototypes_section.title_post", v)} testId="field-products-title-post" />
                    </div>
                    <TextField label="Description" value={data.prototypes_section?.description} onChange={(v) => setPath("prototypes_section.description", v)} testId="field-products-description" textarea rows={2} />
                </FieldGroup>

                <FieldGroup title="Bottom CTA block">
                    <TextField label="Title" value={cta.title} onChange={(v) => setPath("cta_block.title", v)} testId="field-cta-title" />
                    <TextField label="Description" value={cta.description} onChange={(v) => setPath("cta_block.description", v)} testId="field-cta-description" textarea rows={2} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <TextField label="Primary CTA label" value={cta.primary_cta_label} onChange={(v) => setPath("cta_block.primary_cta_label", v)} testId="field-cta-primary-label" />
                        <TextField label="Primary CTA link" value={cta.primary_cta_link} onChange={(v) => setPath("cta_block.primary_cta_link", v)} testId="field-cta-primary-link" />
                        <TextField label="Secondary CTA label" value={cta.secondary_cta_label} onChange={(v) => setPath("cta_block.secondary_cta_label", v)} testId="field-cta-secondary-label" />
                        <TextField label="Secondary CTA link" value={cta.secondary_cta_link} onChange={(v) => setPath("cta_block.secondary_cta_link", v)} testId="field-cta-secondary-link" />
                    </div>
                </FieldGroup>
            </div>

            <div className="mt-8 flex items-center justify-end gap-2">
                <button onClick={save} disabled={saving} data-testid="home-settings-save-bottom" className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? "Saving..." : "Save all"}
                </button>
            </div>
        </div>
    );
}
